type ThemeClassName = "dark" | "light";

export const getCSSVariable = (name: string, theme: ThemeClassName): string => {
  if (typeof window === "undefined") return "";

  const probe = document.createElement("div");
  probe.className = theme;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const value = getComputedStyle(probe).getPropertyValue(name).trim();
  probe.remove();

  return value;
};

// Batched variant of getCSSVariable -- creates a single probe element and reads
// every requested CSS variable off it in one reflow, instead of one probe per
// variable. Prefer this when reading many variables together (e.g. building a
// full appearance/theme map); use getCSSVariable for a single-value read.
export const getCSSVariables = (
  names: string[],
  theme: ThemeClassName,
): Record<string, string> => {
  if (typeof window === "undefined") {
    return Object.fromEntries(names.map(name => [name, ""]));
  }

  const probe = document.createElement("div");
  probe.className = theme;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const computedStyle = getComputedStyle(probe);
  const values = Object.fromEntries(
    names.map(name => [name, computedStyle.getPropertyValue(name).trim()]),
  );
  probe.remove();

  return values;
};

const CSS_VAR_PATTERN = /^var\((--[\w-]+)\)$/;

// Resolves a `var(--token)` theme value to its computed hex for the given
// appearance, since color-picker fields (and anything persisting a color)
// need a literal color, not a CSS variable reference.
export const resolveCssVarColor = (
  color: string | undefined,
  theme: ThemeClassName,
): string | undefined => {
  if (!color) return color;
  const match = CSS_VAR_PATTERN.exec(color);
  if (!match) return color;
  return getCSSVariable(match[1], theme) || color;
};
